import { rem } from 'polished'
import { useCallback, useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import Clickable from 'components/Common/Clickable'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import LineClockIcon from 'LuxKit/Icons/line/LineClockIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Caption from 'LuxKit/Typography/Caption'
import focusStyle from 'styles/utils/focusStyle'
import useToggle from 'hooks/useToggle'

const HoursTable = styled.table`
  border-spacing: 0;

  tr + tr {
    td {
      padding-top: ${rem(8)};
    }
  }
`

const DayNameCell = styled.td`
  padding: 0;
  vertical-align: top;
  min-width: ${rem(85)};
`

const HoursCell = styled.td`
  padding: 0;
  vertical-align: top;
  padding-inline-start: ${rem(16)};
`

const ToggleRow = styled(Clickable)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(8)};
  width: 100%;
  border-radius: ${(props) => props.theme.borderRadius.S};

  &:focus-visible {
    ${focusStyle}
  }
`

interface Props {
  descriptions: Array<string>
}

function splitDescription(description: string): [string, string] {
  const parts = description.split(': ')
  if (parts[1]) {
    parts[1] = parts[1].replace(
      /(\d+:\d+)(\s*(AM|PM))/g,
      (...matches) => `${matches[1]}${matches[3].toLowerCase()}`,
    )
  }
  return [parts[0], parts[1] ?? ''] as [string, string]
}

function toCompactHoursRange(hours: string): string {
  return hours.replace(/(\d+):00(am|pm)/gi, '$1$2')
}

function computeMostCommonHours(rows: Array<[string, string]>): string | undefined {
  const counts = new Map<string, number>()
  rows.forEach(([, hours]) => {
    if (!hours) return
    if (hours.toLowerCase() === 'closed') return
    counts.set(hours, (counts.get(hours) ?? 0) + 1)
  })
  let mostCommon: string | undefined
  let bestCount = 0
  counts.forEach((count, hours) => {
    if (count > bestCount) {
      mostCommon = hours
      bestCount = count
    }
  })
  return mostCommon
}

function OpeningHours({ descriptions }: Props) {
  const intl = useIntl()
  const { value: isOpen, toggle: _toggle } = useToggle()

  const toggle = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      _toggle()
    },
    [_toggle],
  )

  const ArrowIcon = isOpen ? LineAngleUpIcon : LineAngleDownIcon

  const hoursRows = useMemo(
    () => descriptions.map(splitDescription),
    [descriptions],
  )

  const compactMostCommonHours = useMemo(() => {
    const mostCommon = computeMostCommonHours(hoursRows)
    return mostCommon ? toCompactHoursRange(mostCommon) : undefined
  }, [hoursRows])

  const allClosed = hoursRows.length > 0 && hoursRows.every(
    ([, hours]) => !hours || hours.toLowerCase() === 'closed',
  )
  const isOpen24Hours = !allClosed && hoursRows.every(
    ([, hours]) => !hours || hours.toLowerCase() === 'closed' || /24\s*hours/i.test(hours),
  )

  const hoursVaryLabel = intl.formatMessage({
    id: 'D5rmLtvIEu9HEAAnAaha',
    defaultMessage: 'Hours vary',
    description: 'Opening hours placeholder label shown above the expandable day-by-day table',
  })

  const closedLabel = intl.formatMessage({
    id: 'W5UJI6s1d2SHnOExNsgf',
    defaultMessage: 'Closed',
    description: 'Opening hours placeholder label shown when the venue is closed every day of the week',
  })

  const open24HrsLabel = intl.formatMessage({
    id: 'g4XT5Orz4ApfACF5lYsx',
    defaultMessage: 'Open 24 hrs',
    description: 'Opening hours placeholder label shown when the venue operates 24 hours every day',
  })

  const openMostDaysSubtitle = compactMostCommonHours ? intl.formatMessage(
    {
      id: '93xdUzUVpKhTrOfRiRaV',
      defaultMessage: 'open most days {hours}',
      description: 'Subtitle summarising the most common daily hours on the opening hours placeholder',
    },
    { hours: compactMostCommonHours },
  ) : undefined

  let headerLabel = hoursVaryLabel
  let headerSubtitle = openMostDaysSubtitle
  if (allClosed) {
    headerLabel = closedLabel
    headerSubtitle = undefined
  } else if (isOpen24Hours) {
    headerLabel = open24HrsLabel
    headerSubtitle = undefined
  }

  return (
    <BodyTextBlock variant="medium" startIcon={<LineClockIcon />}>
      <div>
        <VerticalSpacer gap={8}>
          <ToggleRow onClick={toggle} aria-expanded={isOpen}>
            <Group as="span" direction="horizontal" gap={8} wrap="wrap" verticalAlign="baseline">
              <span>{headerLabel}</span>
              {headerSubtitle && (
                <Caption variant="large" colour="light-foreground-alt">
                  {headerSubtitle}
                </Caption>
              )}
            </Group>
            <ArrowIcon />
          </ToggleRow>
          {isOpen && (
            <>
              <HoursTable>
                <tbody>
                  {hoursRows.map(([day, hours]) => (
                    <tr key={day}>
                      <DayNameCell>{day}</DayNameCell>
                      <HoursCell>{hours}</HoursCell>
                    </tr>
                  ))}
                </tbody>
              </HoursTable>
              {!allClosed && !isOpen24Hours && (
                <Caption variant="medium" colour="light-foreground-alt">
                  {intl.formatMessage({
                    id: 'ytHCz9ZH9KedZNGCnhr7',
                    defaultMessage: '*Opening hours may vary due to public holidays',
                    description: 'Disclaimer about opening hours potentially varying on public holidays',
                  })}
                </Caption>
              )}
            </>
          )}
        </VerticalSpacer>
      </div>
    </BodyTextBlock>
  )
}

export default OpeningHours
